/**
 * Sync Store - Zustand State for Offline Sync
 *
 * Manages global sync state including:
 * - Online/offline status
 * - Sync progress and status
 * - Per-entity sync tracking
 * - Pending/failed item counts
 *
 * Persisted to localStorage for state recovery after page refresh.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type {
  SyncState,
  SyncActions,
  SyncStore,
  EntityType,
  EntitySyncStatus,
} from '../types/offline.types';

// ============================================
// INITIAL STATE
// ============================================

const initialEntityStatus: EntitySyncStatus = {
  pendingCount: 0,
  failedCount: 0,
  lastSyncAt: null,
  status: 'idle',
};

const initialState: SyncState = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  isSyncing: false,
  pendingCount: 0,
  failedCount: 0,
  lastSyncAt: null,
  syncProgress: 0,
  entityStatus: {},
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Calculate total pending count from entity statuses
 */
function calculateTotalPending(entityStatus: Partial<Record<EntityType, EntitySyncStatus>>): number {
  return Object.values(entityStatus).reduce(
    (sum, status) => sum + (status?.pendingCount ?? 0),
    0
  );
}

/**
 * Calculate total failed count from entity statuses
 */
function calculateTotalFailed(entityStatus: Partial<Record<EntityType, EntitySyncStatus>>): number {
  return Object.values(entityStatus).reduce(
    (sum, status) => sum + (status?.failedCount ?? 0),
    0
  );
}

// ============================================
// STORE CREATION
// ============================================

const useSyncStore = create<SyncStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      actions: {
        /**
         * Update online status
         */
        setOnline: (isOnline: boolean) => {
          set({ isOnline });
        },

        /**
         * Mark sync as started
         */
        startSync: () => {
          set({
            isSyncing: true,
            syncProgress: 0,
          });
        },

        /**
         * Mark sync as completed
         */
        endSync: (success: boolean) => {
          set((state) => ({
            isSyncing: false,
            syncProgress: 100,
            lastSyncAt: success ? Date.now() : state.lastSyncAt,
          }));
        },

        /**
         * Update sync progress (0-100)
         */
        updateProgress: (progress: number) => {
          set({ syncProgress: Math.min(100, Math.max(0, progress)) });
        },

        /**
         * Update status for a specific entity type
         */
        updateEntityStatus: (entity: EntityType, updates: Partial<EntitySyncStatus>) => {
          set((state) => {
            const currentStatus = state.entityStatus[entity] ?? { ...initialEntityStatus };
            const newEntityStatus = {
              ...state.entityStatus,
              [entity]: {
                ...currentStatus,
                ...updates,
              },
            };

            return {
              entityStatus: newEntityStatus,
              pendingCount: calculateTotalPending(newEntityStatus),
              failedCount: calculateTotalFailed(newEntityStatus),
            };
          });
        },

        /**
         * Increment pending count for an entity type
         */
        incrementPending: (entity: EntityType) => {
          set((state) => {
            const currentStatus = state.entityStatus[entity] ?? { ...initialEntityStatus };
            const newEntityStatus = {
              ...state.entityStatus,
              [entity]: {
                ...currentStatus,
                pendingCount: currentStatus.pendingCount + 1,
              },
            };

            return {
              entityStatus: newEntityStatus,
              pendingCount: state.pendingCount + 1,
            };
          });
        },

        /**
         * Decrement pending count for an entity type
         */
        decrementPending: (entity: EntityType) => {
          set((state) => {
            const currentStatus = state.entityStatus[entity] ?? { ...initialEntityStatus };
            const newPendingCount = Math.max(0, currentStatus.pendingCount - 1);
            const newEntityStatus = {
              ...state.entityStatus,
              [entity]: {
                ...currentStatus,
                pendingCount: newPendingCount,
              },
            };

            return {
              entityStatus: newEntityStatus,
              pendingCount: Math.max(0, state.pendingCount - 1),
            };
          });
        },

        /**
         * Increment failed count for an entity type
         */
        incrementFailed: (entity: EntityType) => {
          set((state) => {
            const currentStatus = state.entityStatus[entity] ?? { ...initialEntityStatus };
            const newEntityStatus = {
              ...state.entityStatus,
              [entity]: {
                ...currentStatus,
                failedCount: currentStatus.failedCount + 1,
              },
            };

            return {
              entityStatus: newEntityStatus,
              failedCount: state.failedCount + 1,
            };
          });
        },

        /**
         * Reset failed count for an entity type
         */
        resetFailed: (entity: EntityType) => {
          set((state) => {
            const currentStatus = state.entityStatus[entity] ?? { ...initialEntityStatus };
            const failedDelta = currentStatus.failedCount;
            const newEntityStatus = {
              ...state.entityStatus,
              [entity]: {
                ...currentStatus,
                failedCount: 0,
              },
            };

            return {
              entityStatus: newEntityStatus,
              failedCount: Math.max(0, state.failedCount - failedDelta),
            };
          });
        },

        /**
         * Reset all sync state
         */
        reset: () => {
          set({
            ...initialState,
            isOnline: navigator.onLine,
          });
        },
      },
    }),
    {
      name: 'sync-storage',
      storage: createJSONStorage(() => localStorage),

      // Only persist specific fields (not actions or computed)
      partialize: (state) => ({
        pendingCount: state.pendingCount,
        failedCount: state.failedCount,
        lastSyncAt: state.lastSyncAt,
        entityStatus: state.entityStatus,
      }),

      // Version for migrations
      version: 1,
    }
  )
);

// ============================================
// SELECTORS (Hook Exports)
// ============================================

/**
 * Get online status
 */
export const useIsOnline = (): boolean => {
  return useSyncStore((state) => state.isOnline);
};

/**
 * Get syncing status
 */
export const useIsSyncing = (): boolean => {
  return useSyncStore((state) => state.isSyncing);
};

/**
 * Get total pending count
 */
export const usePendingCount = (): number => {
  return useSyncStore((state) => state.pendingCount);
};

/**
 * Get total failed count
 */
export const useFailedCount = (): number => {
  return useSyncStore((state) => state.failedCount);
};

/**
 * Get sync progress (0-100)
 */
export const useSyncProgress = (): number => {
  return useSyncStore((state) => state.syncProgress);
};

/**
 * Get last sync timestamp
 */
export const useLastSyncAt = (): number | null => {
  return useSyncStore((state) => state.lastSyncAt);
};

/**
 * Get entity-specific sync status
 */
export const useEntitySyncStatus = (entity: EntityType): EntitySyncStatus => {
  return useSyncStore((state) => state.entityStatus[entity] ?? initialEntityStatus);
};

/**
 * Get all sync state
 */
export const useSyncState = (): SyncState => {
  return useSyncStore((state) => ({
    isOnline: state.isOnline,
    isSyncing: state.isSyncing,
    pendingCount: state.pendingCount,
    failedCount: state.failedCount,
    lastSyncAt: state.lastSyncAt,
    syncProgress: state.syncProgress,
    entityStatus: state.entityStatus,
  }));
};

/**
 * Get sync actions
 */
export const useSyncActions = (): SyncActions => {
  return useSyncStore((state) => state.actions);
};

/**
 * Check if there are any pending changes
 */
export const useHasPendingChanges = (): boolean => {
  return useSyncStore((state) => state.pendingCount > 0);
};

/**
 * Check if there are any failed syncs
 */
export const useHasFailedSyncs = (): boolean => {
  return useSyncStore((state) => state.failedCount > 0);
};

// ============================================
// ONLINE/OFFLINE EVENT LISTENERS
// ============================================

/**
 * Initialize online/offline listeners
 * Call this once during app startup
 */
export function initializeSyncListeners(): () => void {
  const actions = useSyncStore.getState().actions;

  const handleOnline = () => {
    console.log('[SyncStore] Device is online');
    actions.setOnline(true);
  };

  const handleOffline = () => {
    console.log('[SyncStore] Device is offline');
    actions.setOnline(false);
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

// ============================================
// EXPORTS
// ============================================

export default useSyncStore;
export type { SyncStore, SyncState, SyncActions };
